import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, getSiteContent, saveSiteContent } from "../firebase";

const MAX_IMAGE_SIZE_BYTES = 750000;

const defaultState = {
  menuSectionTitle: "",
  menuSectionSubtitle: "",
  heroTitle: "",
  heroSubtitle: "",
  menuImageDataUrl: "",
  menuTitle: "",
  menuDescription: "",
  mapTitle: "",
  mapDescription: "",
  galleryTitle: "",
};

function AdminPage({ user, role }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(defaultState);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user || role !== "admin") {
      navigate("/login", { replace: true });
      return;
    }

    let isMounted = true;

    async function loadContent() {
      try {
        const content = await getSiteContent();
        if (isMounted) {
          setForm({ ...defaultState, ...content });
        }
      } catch (loadError) {
        if (isMounted) {
          setStatus(loadError.message || "Could not load content.");
        }
      }
    }

    loadContent();

    return () => {
      isMounted = false;
    };
  }, [navigate, role, user]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setError(`The selected image is too large. Maximum size is ${MAX_IMAGE_SIZE_BYTES / 1000} KB.`);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setError("");
      setForm((current) => ({ ...current, menuImageDataUrl: String(reader.result) }));
      setStatus("Menu image loaded and ready to save.");
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!user || role !== "admin") {
      setError("Only admin users can save content.");
      return;
    }

    setSaving(true);
    setStatus("");
    setError("");

    try {
      await saveSiteContent(form);
      setStatus("Inhalt erfolgreich gespeichert.");
    } catch (saveError) {
      setError(saveError.message || "Inhalt konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    if (auth) {
      await auth.signOut();
    }
    navigate("/login", { replace: true });
  };

  if (!user || role !== "admin") {
    return <div className="auth-page"><p>Checking permissions...</p></div>;
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Admin Panel</h1>
        <button type="button" className="secondary-btn" onClick={handleLogout}>Log out</button>
      </div>

      <form onSubmit={handleSubmit} className="admin-form">
        <section className="admin-section">
          <h2>Haupt Titel</h2>
          <label>
            <span>Titel <span className="field-limit">(Limit 50 Zeichen)</span></span>
            <input name="menuSectionTitle" value={form.menuSectionTitle} onChange={handleChange} maxLength={50} />
          </label>
          <label>
            <span>Beschreibung <span className="field-limit">(Limit 300 Zeichen)</span></span>
            <textarea name="menuSectionSubtitle" value={form.menuSectionSubtitle} onChange={handleChange} rows="3" maxLength={300} />
          </label>
        </section>

        <section className="admin-section">
          <h2>Hero Text über dem Hauptbild </h2>
          <label>
            <span>Titel <span className="field-limit">(Limit 50 Zeichen)</span></span>
            <input name="heroTitle" value={form.heroTitle} onChange={handleChange} maxLength={50} />
          </label>
          <label>
            <span>Untertitel <span className="field-limit">(Limit 300 Zeichen)</span></span>
            <textarea name="heroSubtitle" value={form.heroSubtitle} onChange={handleChange} rows="3" maxLength={300} />
          </label>
        </section>


<section className="admin-section">
          <h2>Menü card</h2>
          <label>
            <span>Menü Section Titel <span className="field-limit">(Limit 50 Zeichen)</span></span>
            <input name="menuTitle" value={form.menuTitle} onChange={handleChange} maxLength={50} />
          </label>
          <label>
            <span>Menü Section Text <span className="field-limit">(Limit 300 Zeichen)</span></span>
            <textarea name="menuDescription" value={form.menuDescription} onChange={handleChange} rows="4" maxLength={300} />
          </label>
        </section>

        <section className="admin-section">
          <h2>Menu image</h2>
          <label>
            Upload menu photo (Max Größe: 750 KB)
            <input type="file" accept="image/*" onChange={handleFileChange} />
          </label>
          {form.menuImageDataUrl && (
            <img src={form.menuImageDataUrl} alt="Menu preview" className="admin-preview-image" />
          )}
          <p className="admin-info">Max Größe: 750 KB.</p>
        </section>


        <section className="admin-section">
          <h2>Map section</h2>
          <label>
            <span>Map Titel <span className="field-limit">(Limit 50 Zeichen)</span></span>
            <input name="mapTitle" value={form.mapTitle} onChange={handleChange} maxLength={50} />
          </label>
          <label>
            <span>Map Beschreibung <span className="field-limit">(Limit 300 Zeichen)</span></span>
            <textarea name="mapDescription" value={form.mapDescription} onChange={handleChange} rows="4" maxLength={300} />
          </label>
        </section>

        <section className="admin-section">
          <h2>Slider section</h2>
          <label>
            <span>Gallery Titel <span className="field-limit">(Limit 50 Zeichen)</span></span>
            <input name="galleryTitle" value={form.galleryTitle} onChange={handleChange} maxLength={50} />
          </label>
          <p className="admin-info">Um die Bilder im Slider zu ändern, wenden Sie sich bitte an den Administrator.</p>
        </section>

        {status && <p className="admin-success">{status}</p>}
        {error && <p className="admin-error">{error}</p>}

        <div className="admin-actions">
          <button type="submit" disabled={saving}>
            {saving ? "Saving..." : "Speichern"}
          </button>
          <a href="/" className="admin-link-button">Main Page öffnen</a>
        </div>
      </form>
    </div>
  );
}

export default AdminPage;
